import { useCallback, useEffect, useMemo, useState } from 'react';
import Axios from '../api/axios';
import { isReportFullAccessUser } from '../utils/reportAccess';
import {
  applyColumnLayout,
  moveColumn,
  moveColumnBy,
  resolveLayoutKey,
  snapshotColumns,
} from '../utils/gridColumnLayout';

function layoutUrl() {
  return `${process.env.REACT_APP_BACKEND_SERVER}grid-layout`;
}

export function useGridColumnLayout(layoutKey, sourceColumns) {
  const canManage = isReportFullAccessUser();
  const viewAudience = canManage ? 'admin' : 'staff';
  const key = useMemo(
    () => resolveLayoutKey(layoutKey, sourceColumns),
    [layoutKey, sourceColumns],
  );
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;
    let cancel = false;
    setDraft(null);
    Axios.get(layoutUrl(), { params: { key } })
      .then((res) => {
        if (cancel) return;
        const columns = res.data?.columns;
        setSaved(Array.isArray(columns) && columns.length ? { columns } : null);
      })
      .catch(() => {
        if (!cancel) setSaved(null);
      });
    return () => {
      cancel = true;
    };
  }, [key]);

  const activeLayout = draft || saved;
  const columns = useMemo(
    () => applyColumnLayout(sourceColumns, activeLayout),
    [sourceColumns, activeLayout],
  );

  const replace = useCallback((next) => {
    setDraft(snapshotColumns(next));
  }, []);

  const resize = useCallback((id, width) => {
    replace(columns.map((col) => (
      col._layoutId === id ? { ...col, width } : col
    )));
  }, [columns, replace]);

  const move = useCallback((fromId, toId) => {
    replace(moveColumn(columns, fromId, toId));
  }, [columns, replace]);

  const moveBy = useCallback((id, delta) => {
    replace(moveColumnBy(columns, id, delta));
  }, [columns, replace]);

  const setVisible = useCallback((id, visible) => {
    replace(columns.map((col) => (
      col._layoutId === id ? { ...col, visible } : col
    )));
  }, [columns, replace]);

  const save = useCallback(async (audience) => {
    const payload = snapshotColumns(columns);
    await Axios.post(layoutUrl(), {
      key,
      audience,
      columns: payload.columns,
    });
    if (audience === viewAudience) setSaved(payload);
    setDraft(null);
  }, [columns, key, viewAudience]);

  const reset = useCallback(async (audience) => {
    await Axios.post(layoutUrl(), { key, audience, reset: true });
    if (audience === viewAudience) {
      setSaved(null);
      setDraft(null);
    }
  }, [key, viewAudience]);

  return {
    columns,
    canManage,
    layoutActive: Boolean(activeLayout?.columns?.length),
    resize,
    move,
    moveBy,
    setVisible,
    save,
    reset,
  };
}
